import { useEffect, useMemo, useRef, useState } from 'react'

import Loading from 'app/common/components/Loader'
import { ReportSection } from 'app/common/templates-next/report-template'
import type { SurveyGrouping } from 'app/common/types/surveys'
import { useSurveyDetails } from 'app/dataProviders/surveys/hooks'

import { emptySurveyFilters } from '../filters'
import { SurveyDetailsChart } from './Chart'
import { getDefaultFilters, SurveyDetailsFilters } from './Filters'
import { SurveyDetailsHeader } from './Header'
import { SurveyDetailsSummary } from './Summary'
import { SurveyGrouping as SurveyGroupingControls } from './Grouping'

import styles from './SurveyDetails.module.scss'

type Props = {
  match: { params: { surveyId: string } }
}

export const SurveyDetails = ({
  match: {
    params: { surveyId },
  },
}: Props) => {
  const [filters, setFilters] = useState(emptySurveyFilters)
  const [filtersDraft, setFiltersDraft] = useState(emptySurveyFilters)
  const [grouping, setGrouping] = useState<SurveyGrouping>('day')
  const scopeInitialized = useRef(false)
  const params = useMemo(() => ({ ...filters, surveyId, grouping }), [filters, surveyId, grouping])
  const { data, loading } = useSurveyDetails({ params })

  useEffect(() => {
    if (!data || scopeInitialized.current) return
    scopeInitialized.current = true
    const initialized = getDefaultFilters(data.survey)
    setFilters(initialized)
    setFiltersDraft(initialized)
  }, [data])

  const survey = data?.survey
  const totals = data?.aggregation || survey?.results

  const applyFilters = () => setFilters(filtersDraft)

  return (
    <div className={styles.page}>
      {!survey && loading && (
        <div className={styles.chartRegion} role="status" aria-label="Загружаем результаты опроса">
          <Loading className={styles.chartLoading} />
        </div>
      )}
      {!survey && !loading && <div className={styles.empty}>{'Опрос не найден'}</div>}

      {survey && (
        <ReportSection
          header={`Опрос “${survey.title}”`}
          headerBar={<SurveyDetailsHeader />}
          skipPadding
        >
          <SurveyDetailsFilters
            survey={survey}
            value={filtersDraft}
            appliedValue={filters}
            onChange={setFiltersDraft}
            onApply={applyFilters}
          />

          <SurveyDetailsSummary survey={survey} totals={totals} />
          <SurveyDetailsChart data={data} loading={loading} />
          <SurveyGroupingControls value={grouping} onChange={setGrouping} />
        </ReportSection>
      )}
    </div>
  )
}
